import React from "react";
import { PortableText, PortableTextComponents, PortableTextBlock } from "@portabletext/react";
import { Heading, HeadingVariant, HeadingPositionVariant } from "@tennr/frontend/components/Heading";
import { BlockQuote } from "@tennr/frontend/components/BlockQuote";
import { DynamicText } from "@tennr/frontend/components/DynamicText";
import "./PortableTextContent.css";

interface PortableTextContentProps {
  title?: string;
  heading?: string;
  content: any[]; // PortableText array
  defaultPosition?: HeadingPositionVariant;
  defaultVariant?: HeadingVariant;
  delay?: number;
  context?: "default" | "case-study";
}

// Map heading levels to our variants
const headingVariantMap: Record<string, HeadingVariant> = {
  h1: "xl",
  h2: "hero",
  h3: "section",
  h4: "section",
  h5: "tertiary",
  h6: "tertiary",
};

// Map alignment styles to position
const alignmentMap: Record<string, HeadingPositionVariant> = {
  left: "left",
  center: "center",
  right: "right",
};

// Create components factory to pass context
const createComponents = (context?: "default" | "case-study"): PortableTextComponents => ({
  block: {
    // Handle headings
    h1: ({ children }) => <DynamicText as="h1">{children}</DynamicText>,
    h2: ({ children }) => <DynamicText as="h2">{children}</DynamicText>,
    h3: ({ children }) => <DynamicText as="h3">{children}</DynamicText>,
    h4: ({ children }) => <DynamicText as="h4">{children}</DynamicText>,
    h5: ({ children }) => <DynamicText as="h5">{children}</DynamicText>,
    h6: ({ children }) => <DynamicText as="h6">{children}</DynamicText>,
    // Handle blockquotes
    blockquote: ({ children }) => <BlockQuote variant={context}>{children}</BlockQuote>,
    // Regular paragraphs
    normal: ({ children }) => <DynamicText as="p">{children}</DynamicText>,
  },
  marks: {
    // Handle text styling
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    underline: ({ children }) => <span style={{ textDecoration: "underline" }}>{children}</span>,
  },
  list: {
    // Handle bullet lists
    bullet: ({ children }) => <ul>{children}</ul>,
    // Handle numbered lists
    number: ({ children }) => <ol>{children}</ol>,
  },
  listItem: {
    // Handle list items - filter out empty ones
    bullet: ({ children }) => {
      const text = children?.toString().trim();
      if (!text || text === '\u200B' || text === '\u200D') {
        return null;
      }
      return <DynamicText as="li">{children}</DynamicText>;
    },
    number: ({ children }) => {
      const text = children?.toString().trim();
      if (!text || text === '\u200B' || text === '\u200D') {
        return null;
      }
      return <DynamicText as="li">{children}</DynamicText>;
    },
  },
});

export const PortableTextContent: React.FC<PortableTextContentProps> = ({
  title,
  heading,
  content,
  defaultPosition = "left",
  defaultVariant = "hero",
  delay = 0,
  context = "default",
}) => {
  // Helper to extract text from a block
  const getBlockText = (block: any): string => {
    if (!block.children) return "";
    return block.children.map((child: any) => child.text || "").join("");
  };

  // Parse content into individual elements
  const parseContent = () => {
    const elements: Array<{
      type: "heading" | "paragraph" | "blockquote";
      variant?: HeadingVariant;
      position?: HeadingPositionVariant;
      title?: string;
      heading?: string;
      body?: string;
      block?: any;
    }> = [];

    // First, add the title as a heading if provided
    if (title || heading) {
      elements.push({
        type: "heading",
        variant: defaultVariant,
        position: defaultPosition,
        title: title || "",
        heading: heading || "",
        body: "",
      });
    }

    // Process each block
    content.forEach((block) => {
      // Handle headings from content - render them directly through PortableText
      // These are additional headings within the content, not the main section heading
      if (block._type === "block" && block.style && block.style.startsWith("h")) {
        elements.push({
          type: "paragraph", // Use paragraph type to render through PortableText
          block: block,
        });
      }
      // Handle blockquotes
      else if (block._type === "block" && block.style === "blockquote") {
        elements.push({
          type: "blockquote",
          block: block,
        });
      }
      // Handle regular paragraphs
      else if (block._type === "block" && (!block.style || block.style === "normal")) {
        const text = getBlockText(block);
        // Skip empty paragraphs and zero-width characters
        if (text.trim() && text.trim() !== '\u200B' && text.trim() !== '\u200D') {
          elements.push({
            type: "paragraph",
            body: text,
            block: block, // Keep the block for rich text rendering
          });
        }
      }
    });

    return elements;
  };

  const elements = parseContent();
  const components = createComponents(context);

  // Render parsed elements
  return (
    <div className="portable-text-content">
      {elements.map((element, index) => {
        if (element.type === "heading") {
          // Only render if there's actual content
          if (element.title || element.heading || element.body) {
            return (
              <Heading
                key={index}
                title={element.title || ""}
                heading={element.heading || ""}
                body={element.body || ""}
                variant={element.variant || defaultVariant}
                position={element.position || defaultPosition}
                dark
              />
            );
          }
          return null;
        } else if (element.type === "paragraph") {
          // Render paragraphs directly, not as Heading components
          return (
            <PortableText key={index} value={[element.block]} components={components} />
          );
        } else if (element.type === "blockquote") {
          return (
            <PortableText key={index} value={[element.block]} components={components} />
          );
        }
        return null;
      })}
    </div>
  );
};